import type {
    ChargeDetail,
    ChargeListItem,
    ChargeOneTimeDiscountItem
} from '../../services/billing';
import { formatMoneyCents } from '../../utils/money.ts';

type AllocatableCredit = Pick<
    ChargeOneTimeDiscountItem,
    'id' | 'amount' | 'expiresAt' | 'createdAt'
>;

export interface CreditUse<T> {
    credit: T;
    usedCents: number;
    returnedCents: number;
}

export interface CreditAllocation<T> {
    uses: CreditUse<T>[];
    usedCents: number;
    returnedCents: number;
}

function expiryOrder(a: string | null, b: string | null): number {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return Date.parse(a) - Date.parse(b);
}

function soonestExpiryFirst(a: AllocatableCredit, b: AllocatableCredit): number {
    return (
        expiryOrder(a.expiresAt, b.expiresAt) ||
        Date.parse(a.createdAt) - Date.parse(b.createdAt) ||
        (a.id < b.id ? -1 : 1)
    );
}

// Mirrors processCharge's credit order (expiresAt nulls last, createdAt, id) and allocateCredits
// in backend/src/routes/api/v2/billing/charges/charges.creditAllocation.ts.
export function allocateCredits<T extends AllocatableCredit>(
    credits: T[],
    totalCents: number
): CreditAllocation<T> {
    return [...credits].sort(soonestExpiryFirst).reduce<CreditAllocation<T>>(
        (allocation, credit) => {
            const creditCents = Math.round(Number(credit.amount) * 100);
            const usedCents = Math.min(creditCents, totalCents - allocation.usedCents);
            const returnedCents = creditCents - usedCents;
            return {
                uses: [...allocation.uses, { credit, usedCents, returnedCents }],
                usedCents: allocation.usedCents + usedCents,
                returnedCents: allocation.returnedCents + returnedCents
            };
        },
        { uses: [], usedCents: 0, returnedCents: 0 }
    );
}

export function creditUses(charge: ChargeDetail) {
    return allocateCredits(charge.oneTimeDiscounts, Math.round(Number(charge.totalAmount) * 100))
        .uses;
}

// Mirrors processCharge: only a charge that credits bring to $0 is settled locally, without Bill.com.
export function settlesWithCredits(usedCents: number, payableCents: number): boolean {
    return usedCents > 0 && payableCents === 0;
}

type ChargeTotals = Pick<ChargeListItem, 'totalAmount' | 'oneTimeDiscountsTotal'>;

function toCents(dollars: string): number {
    return Math.round(Number(dollars) * 100);
}

// Processing uses credits only up to the charge total and returns the rest as a remainder credit.
export function chargeNetCents(charge: ChargeTotals): number {
    return Math.max(0, toCents(charge.totalAmount) - toCents(charge.oneTimeDiscountsTotal));
}

export function oneTimeDiscountAmount(charge: ChargeTotals): string | null {
    const creditsCents = toCents(charge.oneTimeDiscountsTotal);
    if (creditsCents <= 0) return null;
    return `−${formatMoneyCents(Math.min(creditsCents, toCents(charge.totalAmount)))}`;
}

export function oneTimeDiscountTooltip(
    charge: ChargeTotals & Pick<ChargeListItem, 'status'>
): string | undefined {
    const totalCents = toCents(charge.totalAmount);
    const creditsCents = toCents(charge.oneTimeDiscountsTotal);
    if (creditsCents <= 0) return undefined;
    const lines = [`Original price: ${formatMoneyCents(totalCents)}`];
    if (creditsCents <= totalCents) {
        return [...lines, `Discount: −${formatMoneyCents(creditsCents)}`].join('\n');
    }
    lines.push(`Discount: −${formatMoneyCents(totalCents)} of ${formatMoneyCents(creditsCents)}`);
    if (charge.status === 'pending') {
        lines.push(
            `Remainder: ${formatMoneyCents(creditsCents - totalCents)}, kept as a new credit at processing`
        );
    }
    return lines.join('\n');
}
